import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Meta from '../components/Meta';

export default function Home() {
  const [services, setServices] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('https://api.dev.auxo.dk/public/content/services').then(res => res.json()),
      fetch('https://api.dev.auxo.dk/public/content/cases').then(res => res.json())
    ])
      .then(([servicesData, casesData]) => {
        // Sort services to match the exact order: FASTPRIS, SPOT, PORTEFØJLE, UDBUD, BUNDLE, ANDET
        const order = ['fastpris', 'spot', 'portefølje', 'udbud', 'bundle', 'andet'];
        const sortedServices = (servicesData || []).sort((a, b) => {
          const aTitle = a.title?.da?.toLowerCase() || '';
          const bTitle = b.title?.da?.toLowerCase() || '';
          return order.indexOf(aTitle) - order.indexOf(bTitle);
        });
        setServices(sortedServices);
        setCases(casesData || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Fejl ved hentning af CMS data:', err);
        setLoading(false);
      });
  }, []);

  const usps = [
    {
      icon: 'fa-regular fa-handshake-angle',
      title: '100% Uvildig Rådgivning',
      desc: 'Vi modtager ingen returkommissioner eller skjulte gebyrer fra energiselskaberne. Vores rådgivning tjener udelukkende jeres bundlinje.'
    },
    {
      icon: 'fa-regular fa-chart-line-up',
      title: 'Aktiv Markedsovervågning',
      desc: 'Vi overvåger elmarkedet og Nordpool 24/7 for at identificere de mest gunstige indkøbstidspunkter for jeres virksomhed.'
    },
    {
      icon: 'fa-regular fa-file-invoice-dollar',
      title: 'Løbende Fakturakontrol',
      desc: 'Vi kontrollerer jeres elregninger løbende for at sikre, at I kun betaler det aftalte, og at der ikke pålægges fejlgebyrer.'
    },
    {
      icon: 'fa-regular fa-shield-check',
      title: 'Professionel Risikostyring',
      desc: 'Vi hjælper jer med at afveje budgetstabilitet og besparelsespotentiale gennem skræddersyede portefølje- og fastprisaftaler.'
    }
  ];

  const steps = [
    {
      num: '01',
      title: 'Dataindsamling',
      desc: 'Via en simpel fuldmagt indhenter vi jeres historiske forbrugsdata og nuværende elaftaler direkte fra den nationale DataHub.'
    },
    {
      num: '02',
      title: 'Analyse & Strategi',
      desc: 'Vi analyserer jeres forbrugsmønster og risikoprofil for at kortlægge den optimale indkøbsstrategi (spot, fastpris eller portefølje).'
    },
    {
      num: '03',
      title: 'Udbud & Forhandling',
      desc: 'Vi sender jeres forbrug i udbud blandt landets førende leverandører og forhandler dækningsbidrag, tillæg og kontraktvilkår på plads.'
    },
    {
      num: '04',
      title: 'Løbende Optimering',
      desc: 'Vi implementerer aftalen, overvåger markedet for nye muligheder og udfører løbende fakturakontrol, så I altid er optimeret.'
    }
  ];

  return (
    <>
      <Meta 
        title="Forside" 
        description="Uvildig energirådgivning og strategisk energiindkøb til erhverv. Vi optimerer jeres elaftaler gennem udbud, porteføljestyring og skræddersyede strategier."
      />

      {/* 1. Hero Banner */}
      <section className="relative min-h-[85vh] flex items-center justify-center text-center bg-brand-dark text-white overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/57398cfb-6e5d-4ba8-a3b9-c24b8dc48392/b2b-energy-trading-office.jpg" 
            alt="Moderne B2B energihandel kontor og markedsgrafer" 
            className="w-full h-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-slate-950/75" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 py-24 flex flex-col items-center justify-center">
          <span className="text-brand-blue-light uppercase tracking-widest text-xs font-bold mb-4">
            Strategisk Energiindkøb til Erhverv
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
            Uvildig rådgivning, indkøb og <span className="text-brand-blue-light">energistrategi</span>
          </h1>
          <p className="text-slate-200 text-sm md:text-base max-w-3xl mb-10 leading-relaxed">
            Vores produkter giver dig trykhed og stabilitet omkring dine energiomkostninger også selv om markedet svinger, ikke mindst får du den viden til at gennemskue markedet og gøre dig i stand til at agere i dette markedet samt sortere i alle de som vil sælge EL/energi ”meget” billigere end andre.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#ydelser" 
              className="bg-brand-orange hover:bg-brand-orange-hover text-white font-bold tracking-wider text-xs px-8 py-4 rounded-sm uppercase transition-colors duration-200"
            >
              Se vores ydelser
            </a>
            <Link 
              to="/kontakt" 
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold tracking-wider text-xs px-8 py-4 rounded-sm uppercase transition-colors duration-200"
            >
              Få en gratis gennemgang
            </Link>
          </div>
        </div>
      </section>

      {/* 2. Split Call-To-Action (Intro Section) */}
      <section className="bg-brand-light border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="text-left">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange mb-2 block">Hvem er vi</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-800 leading-snug max-w-2xl">
              Din uafhængige partner i <span className="text-brand-orange">strategisk energioptimering</span>
            </h2>
          </div>
          <Link 
            to="/kontakt" 
            className="bg-brand-orange hover:bg-brand-orange-hover text-white font-bold tracking-wider text-xs px-8 py-4 rounded-sm uppercase transition-colors duration-200 whitespace-nowrap"
          >
            Kom i gang nu
          </Link>
        </div>
      </section>

      {/* 3. Asymmetric Content Grid (Feature Split) */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Side: Image */}
          <div className="relative aspect-4/3 rounded-sm overflow-hidden border border-slate-100 shadow-sm">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/57398cfb-6e5d-4ba8-a3b9-c24b8dc48392/case-3.jpg" 
              alt="Moderne erhvervsdomicil med grønne energiløsninger" 
              className="w-full h-full object-cover"
            />
          </div>

          {/* Right Side: Content */}
          <div className="flex flex-col justify-center text-left space-y-6">
            <h2 className="text-3xl md:text-4xl font-black text-slate-800 leading-tight">
              Unik viden om <span className="text-brand-orange">energimarkedet</span>
            </h2>
            <div className="space-y-6 text-brand-muted text-sm leading-relaxed">
              <p className="text-base font-semibold text-slate-800">
                EnergiMæglere.dk tilbyder dig unik viden om energimarkedet og hjælper dig med at finde den løsning der passer bedst.
              </p>
              <div className="space-y-4 border-l-2 border-brand-orange pl-4">
                <p className="text-slate-700 font-medium">
                  • Vi giver dig det bedste der findes på markedet på handels tidspunktet på dine energiomkostninger
                </p>
                <p className="text-slate-700 font-medium">
                  • Vi klarer alt det praktiske arbejdet, så du kan læne dig tilbage og se det positive resultat på bundlinjen
                </p>
                <p className="text-slate-700 font-medium">
                  • Vi står altid til rådighed, hvis du har spørgsmål
                </p>
              </div>
            </div>
            <div className="pt-4">
              <Link 
                to="/om-os" 
                className="bg-brand-orange hover:bg-brand-orange-hover text-white font-bold tracking-wider text-xs px-8 py-4 rounded-sm uppercase transition-colors duration-200 inline-block"
              >
                Læs mere om os
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 4. High-Impact Stats Banner */}
      <section className="bg-brand-orange w-full py-16 text-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-12 justify-center">
          {/* Stat 1 */}
          <div className="flex flex-row md:flex-col items-center space-x-6 md:space-x-0 md:space-y-4 text-left md:text-center">
            <div className="w-20 h-20 rounded-full border border-white/30 flex-shrink-0 flex items-center justify-center text-white text-2xl">
              <i className="fa-regular fa-clock" />
            </div>
            <div className="flex flex-col md:items-center">
              <span className="text-[10px] tracking-widest text-orange-200 font-bold uppercase">Siden 1991</span>
              <h3 className="text-xl font-bold text-white mb-1">25+ Års Erfaring</h3>
              <p className="text-xs text-orange-100/90 leading-normal max-w-[200px] md:mx-auto">
                Vi har beskæftiget os med energi og rådgivning i over 25 år.
              </p>
            </div>
          </div>

          {/* Stat 2 */}
          <div className="flex flex-row md:flex-col items-center space-x-6 md:space-x-0 md:space-y-4 text-left md:text-center">
            <div className="w-20 h-20 rounded-full border border-white/30 flex-shrink-0 flex items-center justify-center text-white text-2xl">
              <i className="fa-regular fa-percent" />
            </div>
            <div className="flex flex-col md:items-center">
              <span className="text-[10px] tracking-widest text-orange-200 font-bold uppercase">Besparelser</span>
              <h3 className="text-xl font-bold text-white mb-1">Mærkbare Resultater</h3>
              <p className="text-xs text-orange-100/90 leading-normal max-w-[200px] md:mx-auto">
                Vores kunder opnår markante besparelser og fuld budgetstabilitet.
              </p>
            </div>
          </div>

          {/* Stat 3 */}
          <div className="flex flex-row md:flex-col items-center space-x-6 md:space-x-0 md:space-y-4 text-left md:text-center">
            <div className="w-20 h-20 rounded-full border border-white/30 flex-shrink-0 flex items-center justify-center text-white text-2xl">
              <i className="fa-regular fa-handshake" />
            </div>
            <div className="flex flex-col md:items-center">
              <span className="text-[10px] tracking-widest text-orange-200 font-bold uppercase">Uafhængighed</span>
              <h3 className="text-xl font-bold text-white mb-1">100% Uvildig</h3>
              <p className="text-xs text-orange-100/90 leading-normal max-w-[200px] md:mx-auto">
                Vi arbejder udelukkende på jeres virksomheds vegne.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Services Section */}
      <section id="ydelser" className="py-24 bg-brand-light scroll-mt-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange">Vores ydelser</span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-800 tracking-tight">
              Strategiske indkøbsaftaler til <span className="text-brand-orange">erhverv</span>
            </h2>
            <p className="text-brand-muted text-sm leading-relaxed max-w-xl mx-auto">
              Vi hjælper jeres virksomhed med at vælge og sammensætte den rette elaftale baseret på jeres risikoprofil og forbrugsmønster.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <i className="fa-regular fa-spinner-third animate-spin text-4xl text-brand-orange" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => {
                const isAndet = service.title?.da?.toUpperCase() === 'ANDET';
                const isBundle = service.title?.da?.toUpperCase() === 'BUNDLE';
                
                if (isAndet) {
                  return (
                    <div 
                      key={service.id}
                      className="bg-brand-dark text-white p-8 rounded-sm border border-brand-dark/20 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                    >
                      <div className="space-y-6">
                        <div className="h-14 w-14 rounded-sm bg-white/10 flex items-center justify-center text-white text-2xl">
                          <i className={service.icon || "fa-regular fa-gears"} />
                        </div>
                        <h3 className="text-lg font-bold text-white tracking-wider">
                          {service.title?.da?.toUpperCase()}
                        </h3>
                        <p className="text-slate-200 text-xs leading-relaxed">
                          {service.description?.da}
                        </p>
                      </div>
                      <div className="pt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-orange-hover">
                        Kontakt os for rådgivning
                      </div>
                    </div>
                  );
                }

                if (isBundle) {
                  return (
                    <div 
                      key={service.id}
                      className="bg-white p-8 rounded-sm border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                    >
                      <div className="space-y-6">
                        <div className="h-14 w-14 rounded-sm bg-brand-light flex items-center justify-center text-brand-orange text-2xl">
                          <i className={service.icon || "fa-regular fa-cubes"} />
                        </div>
                        <h3 className="text-lg font-bold text-brand-dark tracking-wider">
                          {service.title?.da?.toUpperCase()}
                        </h3>
                        <p className="text-brand-muted text-xs leading-relaxed">
                          {service.description?.da}
                        </p>
                      </div>
                      <div className="pt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-orange">
                        Kontakt os for rådgivning
                      </div>
                    </div>
                  );
                }

                return (
                  <Link 
                    key={service.id}
                    to={`/ydelser/${service.slug?.da?.replace('ydelse-', '')}`}
                    className="group bg-white p-8 rounded-sm border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                  >
                    <div className="space-y-6">
                      <div className="h-14 w-14 rounded-sm bg-brand-light flex items-center justify-center text-brand-orange text-2xl group-hover:bg-brand-orange group-hover:text-white transition-colors duration-300">
                        <i className={service.icon} />
                      </div>
                      <h3 className="text-lg font-bold text-brand-dark group-hover:text-brand-orange transition-colors duration-200 tracking-wider">
                        {service.title?.da?.toUpperCase()}
                      </h3>
                      <p className="text-brand-muted text-xs leading-relaxed line-clamp-3">
                        {service.description?.da}
                      </p>
                    </div>
                    <div className="pt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-orange group-hover:translate-x-1 transition-transform duration-200">
                      Læs mere <i className="fa-regular fa-arrow-right text-[10px]" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* 6. Why Choose Us Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange">Hvorfor vælge os</span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-800 tracking-tight">
              Spar penge på energi – <span className="text-brand-orange">uden at bruge tid på det selv</span>
            </h2>
            <p className="text-brand-muted text-sm leading-relaxed max-w-2xl mx-auto">
              Som virksomhed med et betydeligt energiforbrug ved I, at energiomkostninger er en af jeres største driftsudgifter – og at markedet for energi er komplekst, uforudsigeligt og i konstant forandring.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-16">
            {[
              {
                icon: 'fa-regular fa-handshake',
                title: 'Forhandling',
                desc: 'Forhandle de bedste aftaler med leverandører på vegne af jer'
              },
              {
                icon: 'fa-regular fa-chart-line-up',
                title: 'Overvågning',
                desc: 'Overvåge energimarkedet løbende og reagere når priserne bevæger sig'
              },
              {
                icon: 'fa-regular fa-bolt',
                title: 'Optimering',
                desc: 'Optimere jeres energiprofil, så I køber strøm og gas, når det er billigst'
              },
              {
                icon: 'fa-regular fa-leaf',
                title: 'Grøn rådgivning',
                desc: 'Rådgive om grønne energiaftaler og CO₂-reduktion'
              },
              {
                icon: 'fa-regular fa-file-invoice-dollar',
                title: 'Administration',
                desc: 'Håndtere al administration, fakturakontrol og leverandørkontakt'
              }
            ].map((task, idx) => (
              <div key={idx} className="bg-brand-light p-6 rounded-sm border border-slate-100 space-y-4 flex flex-col justify-between">
                <div className="space-y-3">
                  <div className="text-brand-orange text-2xl h-12 w-12 rounded-sm bg-white border border-slate-100 flex items-center justify-center shadow-sm">
                    <i className={task.icon} />
                  </div>
                  <h3 className="text-sm font-bold text-brand-dark">
                    {task.title}
                  </h3>
                  <p className="text-brand-muted text-xs leading-relaxed">
                    {task.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Value Proposition & CTA Box */}
          <div className="bg-brand-light p-8 sm:p-12 rounded-sm border border-slate-100 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <h3 className="text-lg font-bold text-brand-dark">
                Spørgsmålet er ikke, om I kan spare penge på jeres energi. Spørgsmålet er, om I har ressourcerne og markedsindsigten til at gøre det selv.
              </h3>
              <p className="text-brand-muted text-xs leading-relaxed">
                Vores kunder sparer i gennemsnit <strong className="text-brand-orange text-sm font-bold">10–25%</strong> på deres energiregning – uden at skulle bruge intern tid eller ekspertise på det.
              </p>
              <p className="text-brand-muted text-xs leading-relaxed">
                Vi arbejder udelukkende på jeres vegne – ikke på leverandørernes. Det betyder, at I altid kan stole på, at vores anbefalinger er i jeres interesse.
              </p>
            </div>
            <div className="lg:col-span-4 space-y-4 lg:border-l lg:border-slate-200 lg:pl-8">
              <p className="text-brand-dark font-bold text-xs uppercase tracking-wider">
                Uforpligtende gennemgang?
              </p>
              <p className="text-brand-muted text-xs leading-relaxed">
                Må vi kontakte jer for en uforpligtende gennemgang af jeres nuværende energiaftaler? Vi vurderer hurtigt, om der er et besparelsespotentiale.
              </p>
              <Link 
                to="/kontakt" 
                className="w-full text-center bg-brand-orange hover:bg-brand-orange-hover text-white font-bold tracking-wider text-xs px-6 py-4 rounded-sm uppercase transition-colors duration-200 inline-block"
              >
                Få en gratis gennemgang
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Cases Section */}
      <section className="py-24 bg-brand-light border-t border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange">Cases</span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-800 tracking-tight">
              Resultater der kan mærkes på <span className="text-brand-orange">bundlinjen</span>
            </h2>
            <p className="text-brand-muted text-sm leading-relaxed max-w-xl mx-auto">
              Læs hvordan vi har hjulpet andre danske erhvervsvirksomheder med at optimere deres energiindkøb og opnå store besparelser.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <i className="fa-regular fa-spinner-third animate-spin text-4xl text-brand-orange" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {cases.map((item) => (
                <div 
                  key={item.id}
                  className="bg-white rounded-sm border border-slate-100 shadow-sm overflow-hidden flex flex-col justify-between"
                >
                  <div>
                    <div className="relative aspect-video overflow-hidden">
                      <img 
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${item.image?.[0]?.replace('https://assets.auxodev.dk/', '')}`} 
                        alt={item.title?.da} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4 bg-brand-orange text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm">
                        {item.category?.da}
                      </div>
                    </div>
                    <div className="p-8 space-y-4">
                      <h3 className="text-lg font-bold text-brand-dark leading-snug">
                        {item.title?.da}
                      </h3>
                      <p className="text-brand-muted text-xs leading-relaxed line-clamp-3">
                        {item.description?.da}
                      </p>
                    </div>
                  </div>
                  <div className="p-8 pt-0">
                    <Link 
                      to="/kontakt" 
                      className="text-xs font-bold uppercase tracking-wider text-brand-orange hover:text-brand-orange-hover flex items-center gap-2"
                    >
                      Hør om jeres potentiale <i className="fa-regular fa-arrow-right text-[10px]" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 8. Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-20">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange">Processen</span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-800 tracking-tight">
              Sådan <span className="text-brand-orange">fungerer</span> det
            </h2>
            <p className="text-brand-muted text-sm leading-relaxed max-w-xl mx-auto">
              Vi har gjort det nemt og overskueligt at få optimeret jeres energiindkøb. Her er de fire enkle trin.
            </p>
          </div>

          {/* Timeline Layout */}
          <div className="relative">
            {/* Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-brand-orange/20 -translate-y-1/2 z-0" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 relative z-10">
              {steps.map((step, idx) => (
                <div key={idx} className="bg-brand-light p-8 rounded-sm border border-slate-100 shadow-sm space-y-4 relative">
                  <div className="absolute -top-6 left-8 h-12 w-12 rounded-sm bg-brand-orange text-white flex items-center justify-center font-bold text-base shadow-sm">
                    {step.num}
                  </div>
                  <div className="pt-4 space-y-2">
                    <h3 className="text-base font-bold text-brand-dark">
                      {step.title}
                    </h3>
                    <p className="text-brand-muted text-xs leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 9. Contact CTA Section */}
      <section className="relative py-24 bg-brand-dark text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/57398cfb-6e5d-4ba8-a3b9-c24b8dc48392/b2b-energy-trading-office.jpg" 
            alt="Moderne B2B energihandel kontor" 
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-slate-950/40" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
            Klar til at optimere jeres virksomheds <span className="text-brand-blue-light">energiindkøb?</span>
          </h2>
          <p className="text-slate-300 text-base max-w-2xl mx-auto leading-relaxed">
            Kontakt os i dag for en uforpligtende gennemgang af jeres nuværende elaftaler og forbrugsprofil. Vi finder jeres besparelsespotentiale inden for 24 timer.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
            <Link 
              to="/kontakt" 
              className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-4 rounded-sm font-bold uppercase tracking-wider text-xs transition-colors duration-200"
            >
              Få en gratis gennemgang
            </Link>
            <a 
              href="tel:+4550358378" 
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-4 rounded-sm font-bold uppercase tracking-wider text-xs transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <i className="fa-regular fa-phone" /> Ring 50 358 378
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
